// EmailVerify — landing page for /auth/email/verify?token=...
// Posts the token to the backend, gets a session, then routes home.
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";

export default function EmailVerify() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { lang } = useI18n();
  const [state, setState] = useState("verifying"); // verifying | ok | error
  const [error, setError] = useState("");

  useEffect(() => {
    const token = params.get("token");
    if (!token) { setState("error"); setError("Missing token"); return; }
    (async () => {
      try {
        const { data } = await api.post("/auth/email/verify", { token });
        setState("ok");
        toast.success(lang === "es" ? "Correo verificado" : "Email verified");
        // Brief delay so the user sees confirmation
        setTimeout(() => {
          if (data.purpose === "login") navigate("/journey");
          else navigate("/settings");
        }, 800);
      } catch (e) {
        setState("error");
        setError(e.response?.data?.detail || e.message);
      }
    })();
  }, [params, navigate, lang]);

  return (
    <main data-testid="email-verify-page" className="min-h-screen grid place-items-center px-6" style={{ background: "#0B0B0B" }}>
      <div style={{ background: "#171717", border: "1px solid rgba(232,184,75,0.3)", borderRadius: 16, padding: 28, maxWidth: 380, width: "100%", textAlign: "center" }}>
        {state === "verifying" && (
          <>
            <Loader2 className="w-8 h-8 text-gold mx-auto animate-spin" strokeWidth={1.5}/>
            <h1 className="mt-4 text-foreground" style={{ fontFamily: "'Playfair Display', serif", fontSize: 22 }}>
              {lang === "es" ? "Verificando…" : "Verifying…"}
            </h1>
          </>
        )}
        {state === "ok" && (
          <>
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto" strokeWidth={1.5}/>
            <h1 className="mt-4 text-foreground" style={{ fontFamily: "'Playfair Display', serif", fontSize: 22 }}>
              {lang === "es" ? "¡Verificado!" : "Verified!"}
            </h1>
            <p className="mt-2 text-foreground/55 text-sm">{lang === "es" ? "Redirigiendo…" : "Redirecting…"}</p>
          </>
        )}
        {state === "error" && (
          <>
            <AlertCircle className="w-10 h-10 text-destructive mx-auto" strokeWidth={1.5}/>
            <h1 className="mt-4 text-foreground" style={{ fontFamily: "'Playfair Display', serif", fontSize: 22 }}>
              {lang === "es" ? "Enlace inválido" : "Invalid link"}
            </h1>
            <p className="mt-2 text-foreground/55 text-sm">{error}</p>
            <button onClick={() => navigate("/settings")} className="btn-outline mt-5 px-4 py-2 text-xs min-h-[44px]">
              {lang === "es" ? "Ir a Ajustes" : "Go to Settings"}
            </button>
          </>
        )}
      </div>
    </main>
  );
}
